import { Box, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';
import { StatusBadge } from '../StatusBadge';
import { DashboardEmptyState } from './DashboardVisuals';

const { semantic } = colorTokens;

/**
 * Compact list of attention / action-needed items with optional link and badge.
 * @param {{ id: string, title: string, detail?: string, to?: string, tone?: string, badgeLabel?: string }[]} items
 */
export function AttentionList({ items = [], emptyMessage }) {
  if (!items.length) {
    return <DashboardEmptyState message={emptyMessage} />;
  }

  return (
    <Stack spacing={1}>
      {items.map((item) => {
        const content = (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 1.5,
              borderRadius: `${radiusTokens.md}px`,
              border: `1px solid ${semantic.border.subtle}`,
              bgcolor: semantic.bg.input,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              ...(item.to
                ? {
                    '&:hover': {
                      borderColor: semantic.border.default,
                      bgcolor: semantic.action.secondary.bg,
                    },
                  }
                : null),
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: typographyTokens.fontFamily,
                  fontWeight: typographyTokens.fontWeightSemibold,
                  fontSize: 13,
                  lineHeight: '18px',
                  color: semantic.text.primary,
                }}
              >
                {item.title}
              </Typography>
              {item.detail ? (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    lineHeight: '16px',
                    color: semantic.text.muted,
                  }}
                >
                  {item.detail}
                </Typography>
              ) : null}
            </Box>
            {item.badgeLabel ? (
              <StatusBadge
                status={item.tone || 'attention'}
                category="generic"
                apiLabel={item.badgeLabel}
                size="Sm"
                tone={item.tone === 'danger' ? 'Danger' : item.tone === 'success' ? 'Success' : item.tone === 'info' ? 'Info' : 'Warning'}
                sx={{ flexShrink: 0, alignSelf: 'flex-start' }}
              />
            ) : null}
          </Box>
        );

        if (item.to) {
          return (
            <Box
              key={item.id}
              component={RouterLink}
              to={item.to}
              sx={{ textDecoration: 'none', color: 'inherit' }}
            >
              {content}
            </Box>
          );
        }

        return <Box key={item.id}>{content}</Box>;
      })}
    </Stack>
  );
}
